import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { companyInfo } from '../data/mock';

const PrivacyPolicyPage = () => {
  const lastUpdated = 'December 24, 2025';

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-52 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-slate-400">Last Updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none">
            
            <div className="mb-12">
              <p className="text-slate-600 leading-relaxed mb-4">
                {companyInfo.name} ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Information We Collect</h2>
              
              <h3 className="text-lg font-semibold text-slate-800 mb-3">Personal Information</h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                We may collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="text-slate-600 space-y-2 mb-4 list-disc list-inside">
                <li>Fill out a contact form or request a consultation</li>
                <li>Submit a vendor or employment application</li>
                <li>Sign up for our customer portal</li>
                <li>Communicate with us via email or phone</li>
                <li>Enter into a contract for construction services</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mb-4">
                This information may include your name, email address, phone number, mailing address, and project details.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mb-3">Automatically Collected Information</h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                When you visit our website, we may automatically collect certain information about your device, including your IP address, browser type, operating system, access times, and the pages you have viewed. This information is used to analyze trends and improve our website.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. How We Use Your Information</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                We may use the information we collect for various purposes, including:
              </p>
              <ul className="text-slate-600 space-y-2 mb-4 list-disc list-inside">
                <li>Responding to your inquiries and providing customer service</li>
                <li>Processing and managing construction projects</li>
                <li>Providing access to our customer portal</li>
                <li>Evaluating vendor and employment applications</li>
                <li>Sending project updates, invoices, and related communications</li>
                <li>Improving our website and services</li>
                <li>Complying with legal obligations</li>
              </ul>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Information Sharing</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                We may share your information in the following situations:
              </p>
              <ul className="text-slate-600 space-y-2 mb-4 list-disc list-inside">
                <li><strong>Service Providers:</strong> We may share information with third-party vendors who perform services on our behalf, such as our customer portal provider (JobTread), payment processors, and subcontractors involved in your project.</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, your information may be transferred.</li>
                <li><strong>Legal Requirements:</strong> We may disclose information if required by law or in response to legal process.</li>
                <li><strong>Protection of Rights:</strong> We may disclose information to protect our rights, privacy, safety, or property.</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mb-4">
                We do not sell your personal information to third parties.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Customer Portal (JobTread)</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                We use JobTread construction management software to provide our clients with a customer portal for project management and communication. When you use the customer portal, your information is also subject to JobTread's privacy policy.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                Information shared through the portal may include project documents, schedules, photos, invoices, and communications related to your construction project.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Data Security</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Data Retention</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                We retain your personal information for as long as necessary to fulfill the purposes for which it was collected, including to satisfy legal, accounting, or reporting requirements. Project-related information may be retained for the duration required by applicable laws and industry standards.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Your Rights</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Depending on your location, you may have certain rights regarding your personal information, including:
              </p>
              <ul className="text-slate-600 space-y-2 mb-4 list-disc list-inside">
                <li>The right to access your personal information</li>
                <li>The right to correct inaccurate information</li>
                <li>The right to request deletion of your information</li>
                <li>The right to opt-out of certain communications</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mb-4">
                To exercise any of these rights, please contact us using the information provided below.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Cookies and Tracking</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Our website may use cookies and similar tracking technologies to enhance your browsing experience. You can set your browser to refuse cookies, but some features of the website may not function properly.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Third-Party Links</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Children's Privacy</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Our website and services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child, we will take steps to delete it.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Changes to This Policy</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                We may update this Privacy Policy from time to time. The updated version will be indicated by an updated "Last Updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Contact Us</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                If you have questions or concerns about this Privacy Policy or our privacy practices, please contact us:
              </p>
              <ul className="text-slate-600 space-y-2">
                <li><strong>Phone:</strong> {companyInfo.phone}</li>
                <li><strong>Contact Form:</strong> <a href="/contact" className="text-cyan-600 hover:underline">Contact Us</a></li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
